<script setup lang="ts">
/**
 * One note of the annex.
 *
 * The number hangs in the margin rather than running into the title, which is
 * how a printed annex sets them and what lets a reader find "note 12" by
 * running a finger down the edge of the page.
 */
import { computed } from 'vue'

import { isEmptyHtml, sanitiseHtml } from '@/domain/sanitise'
import type { NoteSection } from '@/domain/section'

const props = defineProps<{ section: NoteSection }>()

const safe = computed(() => sanitiseHtml(props.section.html))
const empty = computed(() => isEmptyHtml(props.section.html))
</script>

<template>
  <section class="note">
    <h3 class="note__head">
      <span v-if="section.number" class="note__number">{{ section.number }}</span>
      <span class="note__title">{{ section.title }}</span>
    </h3>

    <p v-if="empty" class="note__empty">&nbsp;</p>
    <!-- eslint-disable-next-line vue/no-v-html -->
    <div v-else class="note__body" v-html="safe" />
  </section>
</template>

<style scoped>
.note {
  margin: 1.4em 0 1em;
  break-inside: avoid-column;
}

.note__head {
  display: flex;
  align-items: baseline;
  gap: 0.6em;
  margin: 0 0 0.4em;
}

/* Hung into the margin, so the numbers line up down the page whatever the
   titles do. */
.note__number {
  flex: none;
  min-width: 2.2em;
  margin-left: -2.8em;
  color: var(--page-accent);
  font-variant-numeric: tabular-nums lining-nums;
  text-align: right;
}

.note__title {
  flex: 1;
}

.note__empty {
  margin: 0;
}
</style>
